import { t } from '../../i18n'
import { NamedRecord } from '../shared/selectOptions'

export type StepKind = 'add_product' | 'add_sauce' | 'set_stove_level' | 'cook'

export const STEP_KINDS: StepKind[] = [
  'add_product',
  'add_sauce',
  'set_stove_level',
  'cook'
]

export interface LocationOption {
  id: number
  name: string
  organizationName: string | null
}

export interface SessionInfo {
  serviceType: string
  state: 'pending' | 'active'
}

export interface CookingUnitOption {
  id: number
  slotNumber: number
}

export interface ProductOption extends NamedRecord {
  liquid: boolean
  inStockAmount: number
}

export type MoveModeOption = NamedRecord

export interface TestCaseStep {
  kind: StepKind
  productId: number | null
  productName?: string | null
  amount: number | null
  moveModeId: number | null
  moveModeName?: string | null
  durationSeconds: number | null
  stoveLevel: number | null
}

export interface TestCase {
  id: number
  name: string
  notes: string | null
  cookingUnitId: number
  unloadMoveModeId: number
  steps: TestCaseStep[]
}

export interface TestCaseAttributes {
  name: string
  notes: string
  cookingUnitId: number | null
  unloadMoveModeId: number | null
  steps: TestCaseStep[]
}

export interface LocationContext {
  session: SessionInfo | null
  cookingUnits: CookingUnitOption[]
  products: ProductOption[]
  cookingMoveModes: MoveModeOption[]
  unloadMoveModes: MoveModeOption[]
  stoveLevels: number[]
  testCases: TestCase[]
}

const productName = (step: TestCaseStep, context: LocationContext): string =>
  step.productName ??
  context.products.find((product) => product.id === step.productId)?.name ??
  '?'

const moveModeName = (step: TestCaseStep, context: LocationContext): string =>
  step.moveModeName ??
  context.cookingMoveModes.find((mode) => mode.id === step.moveModeId)?.name ??
  '?'

// Saved steps carry their names from the server; steps still being composed resolve them
// from the pickers' option lists.
export const describeStep = (
  step: TestCaseStep,
  context: LocationContext,
  style: 'compact' | 'verbose'
): string => {
  switch (step.kind) {
    case 'add_product':
    case 'add_sauce':
      return style === 'verbose'
        ? `${t(`move_mode_test_cases.operations.${step.kind}`)} ${productName(step, context)} ${step.amount ?? '?'} g`
        : productName(step, context)
    case 'set_stove_level':
      return `${t('move_mode_test_cases.operations.set_stove_level')} ${step.stoveLevel ?? '?'}`
    case 'cook':
      return style === 'verbose'
        ? `${t('move_mode_test_cases.operations.cook')}: ${moveModeName(step, context)} ${step.durationSeconds ?? '?'} s`
        : `${moveModeName(step, context)} ${step.durationSeconds ?? '?'}s`
    default: {
      const unreachable: never = step.kind
      return unreachable
    }
  }
}
